import { Col, DatePicker, Input, Row, Select, Spin, Table, Tabs } from "antd";
import dayjs from "dayjs";
import { useEffect, useRef, useState } from "react";
import { ContentBox } from "../../components/layout/content";
import { LoginedLayout } from "../../components/layout/loginLayout";
import { GET } from "../../services";
import { CHECKER_SUM_ORDER_BY_DATE, CHECKER_SUM_SKU_BY_DATE } from "../../services/path";
import "./style.css";

interface RowData {
  brand: string;
  platform: string;
  logistic: string;
  waiting: number;
  ready: number;
  pick: number;
  packed: number;
  checked_out: number;
  shipping: number;
  complete: number;
  cancel: number;
  ret: number;
}
interface skuData {
  sku: string;
  ready: number;
  pick: number;
  packed: number;
  checked_out: number;
  shipping: number;
  complete: number;
  waiting: number;
}

const ReportOrder = () => {
  const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(dayjs());
  const [loading, setLoading] = useState(false);
  const [dataOrder, setDataOrder] = useState<RowData[]>([]);
  const [dataSku, setDataSku] = useState<skuData[]>([]);
  const [filteredData, setFilteredData] = useState(dataSku);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState("1");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const tableRef = useRef<HTMLDivElement | null>(null);
  const { TabPane } = Tabs;
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  const pageOptions = ["10", "25", "50", "100"];
  const handleSearch = (value: string) => {
    setSearchValue(value.trim());
  };

  useEffect(() => {
    const trimmedSearchValue = searchValue || "";
    if (!trimmedSearchValue) {
      setFilteredData(dataSku);
    } else {
      const filteredList = dataSku.filter((item) =>
        item.sku.toUpperCase().includes(searchValue.toUpperCase()),
      );
      setFilteredData(filteredList);
    }
  }, [searchValue, dataSku]);

  const fetchOrderData = async () => {
    try {
      setLoading(true);
      const searchOrder = CHECKER_SUM_ORDER_BY_DATE(startDate);
      const result = await GET(searchOrder);
      setDataOrder(result);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSkuData = async () => {
    try {
      setLoading(true);
      const searchSku = CHECKER_SUM_SKU_BY_DATE(startDate);
      const result = await GET(searchSku);
      setDataSku(result);
    } catch (error) {
      console.error("Error fetching SKU data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrderData();
    fetchSkuData();
  }, [startDate]);

  useEffect(() => {
    if (tableRef.current) {
      const table = tableRef.current;
      const isOverflowing = table.scrollWidth > table.clientWidth;
      table.style.overflowX = isOverflowing ? "scroll" : "auto";
    }
  }, [dataOrder]);

  dataOrder.sort((a, b) => {
    const brandComparison = a.brand.localeCompare(b.brand);
    if (brandComparison !== 0) {
      return brandComparison;
    }

    return a.platform.localeCompare(b.platform);
  });

  const brandRowSpans: Record<string, number> = {};
  const platformRowSpans: Record<string, number> = {};
  dataOrder.forEach((row) => {
    brandRowSpans[row.brand] = (brandRowSpans[row.brand] || 0) + 1;
    platformRowSpans[row.platform] = (platformRowSpans[row.platform] || 0) + 1;
  });

  const mergedData: RowData[] = [];
  dataOrder.forEach((row, index) => {
    if (index === 0 || row.brand !== dataOrder[index - 1].brand) {
      mergedData.push({
        brand: row.brand,
        platform: row.platform,
        logistic: row.logistic,
        waiting: row.waiting,
        ready: row.ready,
        pick: row.pick,
        packed: row.packed,
        checked_out: row.checked_out,
        shipping: row.shipping,
        complete: row.complete,
        ret: row.ret,
        cancel: row.cancel,
      });
    } else if (row.platform !== dataOrder[index - 1].platform) {
      mergedData.push({
        brand: "",
        platform: row.platform,
        logistic: row.logistic,
        waiting: row.waiting,
        ready: row.ready,
        pick: row.pick,
        packed: row.packed,
        checked_out: row.checked_out,
        shipping: row.shipping,
        complete: row.complete,
        ret: row.ret,
        cancel: row.cancel,
      });
    } else {
      mergedData.push({
        brand: "",
        platform: "",
        logistic: row.logistic,
        waiting: row.waiting,
        ready: row.ready,
        pick: row.pick,
        packed: row.packed,
        checked_out: row.checked_out,
        shipping: row.shipping,
        complete: row.complete,
        ret: row.ret,
        cancel: row.cancel,
      });
    }
  });

  const dynamicColumns = [
    {
      title: <div style={{ textAlign: "center" }}>Brand</div>,
      dataIndex: "brand",
      key: "brand",
      render: (text: string, record: RowData) => {
        if (brandRowSpans[record.brand]) {
          return {
            children: <strong>{text}</strong>,
            props: {
              rowSpan: brandRowSpans[record.brand],
            },
          };
        }
        return {
          children: <strong>{text}</strong>,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Platform</div>,
      dataIndex: "platform",
      key: "platform",
      render: (text: string, record: RowData) => {
        if (brandRowSpans[record.brand]) {
          return {
            children: <strong>{text}</strong>,
            props: {
              rowSpan: brandRowSpans[record.brand],
            },
          };
        }
        return {
          children: <strong>{text}</strong>,
          props: {
            rowSpan: 0,
          },
        };
      },
    },
    {
      title: <div style={{ textAlign: "center" }}>Logistic</div>,
      dataIndex: "logistic",
      key: "logistic",
    },

    {
      title: <div style={{ textAlign: "center" }}>Pick</div>,
      dataIndex: "pick",
      key: "pick",
    },
    {
      title: <div style={{ textAlign: "center" }}>Packed</div>,
      dataIndex: "packed",
      key: "packed",
    },

    {
      title: <div style={{ textAlign: "center" }}>Complate</div>,
      dataIndex: "complete",
      key: "complete",
    },
  ];

  const skuColumns = [
    {
      title: <div style={{ textAlign: "center" }}>SKU ID</div>,
      dataIndex: "sku",
      key: "sku",
      sorter: (a: skuData, b: skuData) => a.sku.localeCompare(b.sku),
    },

    {
      title: <div style={{ textAlign: "center" }}>Pick</div>,
      dataIndex: "pick",
      key: "pick",
      sorter: (a: skuData, b: skuData) => a.pick - b.pick,
    },
    {
      title: <div style={{ textAlign: "center" }}>Packed</div>,
      dataIndex: "packed",
      key: "packed",
      sorter: (a: skuData, b: skuData) => a.packed - b.packed,
    },

    {
      title: <div style={{ textAlign: "center" }}>Complate</div>,
      dataIndex: "complete",
      key: "complete",
      sorter: (a: skuData, b: skuData) => a.complete - b.complete,
    },
  ];

  return (
    <LoginedLayout>
      <ContentBox>
        {loading && <Spin size="large" style={{ position: "absolute", top: "50%", left: "50%" }} />}
        <div
          style={{ marginBottom: "16px", borderBottom: "1px solid #e8e8e8", paddingBottom: "8px" }}
        >
          <h2 style={{ marginBottom: "8px" }}>Report Page</h2>
        </div>
        <span>Date Range:</span>
        <DatePicker
          className="small-date-picker"
          style={{ marginLeft: "8px", marginBottom: "20px" }}
          showTime={false} // Add this line to display a single calendar
          defaultValue={dayjs()} // Set the default value to the current date
          onChange={(dates) => {
            if (dates) {
              setStartDate(dates);
            } else {
              setStartDate(null);
            }
          }}
        />
        <Row gutter={[16, 16]} style={{ marginBottom: "15px" }}>
          <Col xs={14} sm={12} md={14} lg={18} xl={18}>
            <span>Show: </span>
            <Select
              value={pageSize.toString()}
              onChange={(value) => handlePageSizeChange(parseInt(value))}
            >
              {pageOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
          </Col>
          <Col xs={10} sm={12} md={10} lg={6} xl={6}>
            <Input.Search
              placeholder="Enter SKU ID"
              onSearch={handleSearch}
              value={searchValue} // Set the value of the search input
              onChange={(e) => setSearchValue(e.target.value)} // Update searchValue on input change
              className={`fade-in-out ${activeTab === "1" ? "" : "visible"}`}
            />
          </Col>
        </Row>
        <Tabs
          defaultActiveKey="1"
          type="card"
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key)}
        >
          <TabPane tab="Order" key="1">
            <div ref={tableRef}>
              <Table
                columns={dynamicColumns}
                dataSource={mergedData}
                pagination={false}
                scroll={{ x: 1500 }}
              />
            </div>
          </TabPane>
          <TabPane tab="SKU" key="2">
            <div ref={tableRef}>
              <Table
                columns={skuColumns}
                dataSource={filteredData}
                pagination={{
                  pageSize: pageSize, // Set the number of rows per page to 25
                  showSizeChanger: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
                  onShowSizeChange: (current, size) => handlePageSizeChange(size),
                  current: currentPage,
                  onChange: (page) => setCurrentPage(page),
                  total: filteredData.length,
                }}
                scroll={{ x: 1500 }}
              />
            </div>
          </TabPane>
        </Tabs>
      </ContentBox>
    </LoginedLayout>
  );
};

export default ReportOrder;
